<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import CopilotAccountConfigAPI from 'dashboard/api/copilot/accountConfig';
import CopilotAuditEventsAPI from 'dashboard/api/copilot/auditEvents';
import CopilotRoutinesAPI from 'dashboard/api/copilot/routines';

const { t } = useI18n();

const isFetching = ref(false);
const isSaving = ref(false);
const allTools = ref([]);
const auditEvents = ref([]);
const routines = ref([]);

const form = reactive({
  enabled: true,
  quick_mode_enabled: false,
  routine_limit: 3,
  retention_days: 60,
  disabled_tool_ids: [],
  limits: {
    user_bulk_limit: 50,
    admin_bulk_limit: 500,
  },
});

const disabledToolSet = computed(() => new Set(form.disabled_tool_ids));

// SiteUP CRM (2026-09-25): foto do que veio do servidor, para os Ajustes da
// IA perguntarem antes de sair com mudança não salva.
const snapshot = ref(null);
const formJson = () => JSON.stringify(form);
const isDirty = computed(
  () => snapshot.value !== null && formJson() !== snapshot.value
);

const fetchConfig = async () => {
  isFetching.value = true;
  try {
    const [{ data: config }, { data: audit }, { data: routineData }] =
      await Promise.all([
        CopilotAccountConfigAPI.get(),
        CopilotAuditEventsAPI.get({ per_page: 8 }),
        CopilotRoutinesAPI.get(),
      ]);

    form.enabled = config.enabled;
    form.quick_mode_enabled = config.quick_mode_enabled;
    form.routine_limit = config.routine_limit;
    form.retention_days = config.retention_days;
    form.disabled_tool_ids = config.disabled_tool_ids || [];
    form.limits = {
      user_bulk_limit: config.limits?.user_bulk_limit || 50,
      admin_bulk_limit: config.limits?.admin_bulk_limit || 500,
    };
    allTools.value = config.all_tools || [];
    auditEvents.value = audit.audit_events || [];
    routines.value = routineData.routines || [];
    snapshot.value = formJson();
  } catch (error) {
    useAlert(error.message);
  } finally {
    isFetching.value = false;
  }
};

const toggleTool = toolId => {
  if (disabledToolSet.value.has(toolId)) {
    form.disabled_tool_ids = form.disabled_tool_ids.filter(id => id !== toolId);
  } else {
    form.disabled_tool_ids = [...form.disabled_tool_ids, toolId];
  }
};

const saveConfig = async () => {
  isSaving.value = true;
  try {
    await CopilotAccountConfigAPI.update({
      account_config: {
        enabled: form.enabled,
        quick_mode_enabled: form.quick_mode_enabled,
        routine_limit: Number(form.routine_limit),
        retention_days: Number(form.retention_days),
        disabled_tool_ids: form.disabled_tool_ids,
        limits: {
          user_bulk_limit: Number(form.limits.user_bulk_limit),
          admin_bulk_limit: Number(form.limits.admin_bulk_limit),
        },
      },
    });
    useAlert(t('CAPTAIN_SETTINGS.API.SUCCESS'));
    await fetchConfig();
    return true;
  } catch (error) {
    useAlert(error.message);
    return false;
  } finally {
    isSaving.value = false;
  }
};

const discard = () => {
  if (snapshot.value === null) return;
  Object.assign(form, JSON.parse(snapshot.value));
};

defineExpose({ isDirty, save: saveConfig, discard });

const formatDate = value => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

onMounted(fetchConfig);
</script>

<template>
  <div class="grid gap-4">
    <div
      v-if="isFetching"
      class="rounded-2xl border border-n-weak bg-n-solid-1 p-4 text-sm text-n-slate-11"
    >
      {{ t('CAPTAIN_SETTINGS.COPILOT.LOADING') }}
    </div>

    <template v-else>
      <div class="rounded-2xl border border-n-weak bg-n-solid-1 p-4 space-y-4">
        <div class="flex items-start justify-between gap-4">
          <div>
            <h4 class="text-sm font-medium text-n-slate-12">
              {{ t('CAPTAIN_SETTINGS.COPILOT.ENABLED_TITLE') }}
            </h4>
            <p class="text-sm text-n-slate-11 mt-0.5">
              {{ t('CAPTAIN_SETTINGS.COPILOT.ENABLED_DESCRIPTION') }}
            </p>
          </div>
          <Switch v-model="form.enabled" />
        </div>

        <div class="flex items-start justify-between gap-4">
          <div>
            <h4 class="text-sm font-medium text-n-slate-12">
              {{ t('CAPTAIN_SETTINGS.COPILOT.QUICK_MODE_TITLE') }}
            </h4>
            <p class="text-sm text-n-slate-11 mt-0.5">
              {{ t('CAPTAIN_SETTINGS.COPILOT.QUICK_MODE_DESCRIPTION') }}
            </p>
          </div>
          <Switch v-model="form.quick_mode_enabled" />
        </div>
      </div>

      <div class="rounded-2xl border border-n-weak bg-n-solid-1 p-4">
        <h4 class="text-sm font-medium text-n-slate-12">
          {{ t('CAPTAIN_SETTINGS.COPILOT.LIMITS_TITLE') }}
        </h4>
        <div class="grid grid-cols-1 md:grid-cols-4 gap-3 mt-3">
          <label class="grid gap-1 text-xs text-n-slate-11">
            {{ t('CAPTAIN_SETTINGS.COPILOT.USER_BULK_LIMIT') }}
            <input
              v-model="form.limits.user_bulk_limit"
              type="number"
              min="1"
              class="reset-base rounded-xl border border-n-weak bg-n-alpha-2 px-3 py-2 text-sm text-n-slate-12"
            />
          </label>
          <label class="grid gap-1 text-xs text-n-slate-11">
            {{ t('CAPTAIN_SETTINGS.COPILOT.ADMIN_BULK_LIMIT') }}
            <input
              v-model="form.limits.admin_bulk_limit"
              type="number"
              min="1"
              class="reset-base rounded-xl border border-n-weak bg-n-alpha-2 px-3 py-2 text-sm text-n-slate-12"
            />
          </label>
          <label class="grid gap-1 text-xs text-n-slate-11">
            {{ t('CAPTAIN_SETTINGS.COPILOT.ROUTINE_LIMIT') }}
            <input
              v-model="form.routine_limit"
              type="number"
              min="0"
              class="reset-base rounded-xl border border-n-weak bg-n-alpha-2 px-3 py-2 text-sm text-n-slate-12"
            />
          </label>
          <label class="grid gap-1 text-xs text-n-slate-11">
            {{ t('CAPTAIN_SETTINGS.COPILOT.RETENTION_DAYS') }}
            <input
              v-model="form.retention_days"
              type="number"
              min="1"
              max="60"
              class="reset-base rounded-xl border border-n-weak bg-n-alpha-2 px-3 py-2 text-sm text-n-slate-12"
            />
          </label>
        </div>
      </div>

      <div class="rounded-2xl border border-n-weak bg-n-solid-1 p-4">
        <h4 class="text-sm font-medium text-n-slate-12">
          {{ t('CAPTAIN_SETTINGS.COPILOT.TOOLS_TITLE') }}
        </h4>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-2 mt-3">
          <label
            v-for="tool in allTools"
            :key="tool.id"
            class="flex items-center gap-2 rounded-xl border border-n-weak px-3 py-2 text-sm text-n-slate-11"
          >
            <input
              type="checkbox"
              :checked="!disabledToolSet.has(tool.id)"
              class="reset-base"
              @change="toggleTool(tool.id)"
            />
            <span class="truncate">{{ tool.id }}</span>
          </label>
        </div>
      </div>

      <div class="flex justify-end">
        <Button
          :label="t('CAPTAIN_SETTINGS.COPILOT.SAVE')"
          teal
          :is-loading="isSaving"
          @click="saveConfig"
        />
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div class="rounded-2xl border border-n-weak bg-n-solid-1 p-4">
          <h4 class="text-sm font-medium text-n-slate-12">
            {{ t('CAPTAIN_SETTINGS.COPILOT.ROUTINES_TITLE') }}
          </h4>
          <div class="mt-3 space-y-2">
            <p v-if="!routines.length" class="text-sm text-n-slate-11">
              {{ t('CAPTAIN_SETTINGS.COPILOT.ROUTINES_EMPTY') }}
            </p>
            <div
              v-for="routine in routines"
              :key="routine.id"
              class="rounded-xl bg-n-alpha-2 px-3 py-2"
            >
              <div class="text-sm font-medium text-n-slate-12 truncate">
                {{ routine.name }}
              </div>
              <div class="text-xs text-n-slate-11">
                {{ routine.status }}
              </div>
            </div>
          </div>
        </div>

        <div class="rounded-2xl border border-n-weak bg-n-solid-1 p-4">
          <h4 class="text-sm font-medium text-n-slate-12">
            {{ t('CAPTAIN_SETTINGS.COPILOT.AUDIT_TITLE') }}
          </h4>
          <div class="mt-3 space-y-2">
            <p v-if="!auditEvents.length" class="text-sm text-n-slate-11">
              {{ t('CAPTAIN_SETTINGS.COPILOT.AUDIT_EMPTY') }}
            </p>
            <div
              v-for="event in auditEvents"
              :key="event.id"
              class="rounded-xl bg-n-alpha-2 px-3 py-2"
            >
              <div class="text-sm text-n-slate-12">
                {{ event.summary || event.event_type }}
              </div>
              <div class="flex items-center gap-1 text-xs text-n-slate-11">
                <span>{{ event.status }}</span>
                <span>{{ formatDate(event.occurred_at) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>
